import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Eye, RefreshCw } from "lucide-react";
import api from "../lib/api";
import Header from "../components/app/Header";
import LockedFeature from "../components/app/LockedFeature";
import { AuditRow } from "../components/app/audit/AuditRow";
import { AuditSummary } from "../components/app/audit/AuditSummary";
import { useAuditSave } from "../components/app/audit/useAuditSave";
import { useUnsavedGuard } from "../components/app/audit/useUnsavedGuard";
import { calculateAudit, MONTHLY_IDS, rupees } from "../lib/auditMath";
import "./GapsAudit.css";

export default function GapsAudit() {
  const [config, setConfig] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [denied, setDenied] = useState(false);
  const saveState = useAuditSave(setConfig);
  const { initialize, dirty } = saveState;
  useUnsavedGuard(dirty);
  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const response = await api.get("/gaps-audit/fixed", { timeout: 20000 });
      setData(response.data); setConfig(response.data.config); initialize(response.data);
    } catch (err) {
      if (err.response?.status === 403) setDenied(true);
      else setError(true);
    } finally { setLoading(false); }
  }, [initialize]);
  useEffect(() => { load(); }, [load]);
  const calculation = config && calculateAudit(config, saveState.records);
  if (denied) return <LockedFeature featureName="Gaps Audit" description="Gaps Audit is available to active members. Your account access is currently revoked." testid="gaps-audit-locked" />;
  return <div className="min-h-screen" data-testid="gaps-audit-page">
    <Header />
    <main className="fixed-audit">
      <Link to="/dashboard" data-testid="gaps-audit-back-link" className="audit-back"><ArrowLeft size={14} /> Dashboard / Gaps Audit</Link>
      <header className="audit-heading">
        <div className="audit-eyebrow">PRIVATE BUSINESS WORKSHEET</div>
        <h1 className="font-display" data-testid="audit-title">Gaps Audit</h1>
        {!loading && !error && data && !data.can_edit && <div className="audit-view-only" data-testid="audit-view-only"><Eye size={14} /> View only — editing is not enabled for your account.</div>}
        <p data-testid="audit-header-note">{data && !data.can_edit && !loading ? "Review each gap, its cost formula and the growth it can unlock." : "Enter your cost for each gap. 'What I need to implement' is prefilled. Growth is calculated from your cost."}</p>
        <p className="audit-revenue-note" data-testid="audit-revenue-note">Monthly revenue = annual turnover ÷ 12.</p>
        {!loading && !error && data?.revenue?.range && <p data-testid="audit-revenue-range">Your annual revenue range: <span>{data.revenue.range}</span></p>}
        {!loading && !error && data?.revenue?.monthly != null && <p data-testid="audit-revenue-reference">Monthly revenue reference: {data.revenue.approximate ? "approximately " : ""}{rupees(data.revenue.monthly)}</p>}
      </header>
      {loading ? <div className="audit-skeleton" data-testid="gaps-audit-loading" role="status" aria-label="Loading your saved audit">
        {[0, 1, 2, 3].map((i) => <div key={i} />)}
      </div> : error ? <div className="audit-load-error" data-testid="audit-load-error" role="alert">
        <p>Couldn't load your saved audit.</p><button data-testid="audit-load-retry" onClick={load}><RefreshCw size={16} /> Retry</button>
      </div> : <>
        <AuditSummary calculation={calculation} />
        <table className="audit-table" data-testid="gaps-audit-table">
          <colgroup><col style={{ width: "15%" }} /><col style={{ width: "21%" }} /><col style={{ width: "19%" }} /><col style={{ width: "26%" }} /><col style={{ width: "19%" }} /></colgroup>
          <thead><tr>{["Gap found", "Cost formula", "What it costs me (₹/month)", "What I need to implement", "Growth it unlocks (₹/month)"].map((label, index) => <th scope="col" data-testid={`audit-column-${index}`} key={label}>{label}</th>)}</tr></thead>
          <tbody>{config.rows.filter((rule) => MONTHLY_IDS.includes(rule.id)).map((rule) => <AuditRow key={rule.id} rule={rule} result={calculation.rows[rule.id]} rules={config.rules} saveState={saveState} readOnly={!data.can_edit} />)}</tbody>
        </table>
      </>}
    </main>
  </div>;
}